import { useState } from 'react';

// Material UI
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    Typography,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    Switch,
    Paper,
    Grid,
} from '@mui/material';

import { Section, Header, Content } from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { useTextInput, useNumberInput } from 'hooks/forms';

/**
 * If allowEdit = false, only the name of the host_pricing record can be changed
 * If allowDelete = false, the host_pricing record cannot be deleted
 */

export default function HostPricingForm(props) {
    const {
        onSubmit,
        onCancel,
        onDelete,
        allowDelete,
        allowEdit,
        defaultValues,
        loading,
        hostName,
    } = props;

    const theme = useTheme();

    // Input Values
    const name = useTextInput({ defaultValue: defaultValues?.name ?? undefined, maxLength: 100 });
    const b4tFlat = useNumberInput({
        defaultValue: defaultValues?.b4tFlat ?? undefined,
        disabled: !allowEdit,
    });
    const b4tPercent = useNumberInput({
        defaultValue: defaultValues?.b4tPercent ?? undefined,
        disabled: !allowEdit,
    });
    const feeMin = useNumberInput({
        defaultValue: defaultValues?.feeMin ?? undefined,
        disabled: defaultValues?.enableFeeCaps === true && allowEdit ? false : true,
    });
    const feeMax = useNumberInput({
        defaultValue: defaultValues?.feeMax ?? undefined,
        disabled: defaultValues?.enableFeeCaps === true && allowEdit ? false : true,
    });
    const ccPercent = useNumberInput({
        defaultValue: defaultValues?.ccPercent ?? undefined,
        disabled: !allowEdit,
    });
    const ccFlatFee = useNumberInput({
        defaultValue: defaultValues?.ccFlatFee ?? undefined,
        disabled: !allowEdit,
    });
    const posPercent = useNumberInput({
        defaultValue: defaultValues?.posPercent ?? undefined,
        disabled: !allowEdit,
    });
    const reservePercent = useNumberInput({
        defaultValue: defaultValues?.reservePercent ?? undefined,
        disabled: defaultValues?.enableScheduledPayouts === true && allowEdit ? false : true,
    });

    // Absorbing Fees
    const absorbsB4tFees = useTextInput({ defaultValue: defaultValues?.absorbsB4tFees ?? '' }); // '' | 'customer' | 'host'
    const absorbsCcFees = useTextInput({ defaultValue: defaultValues?.absorbsCcFees ?? '' }); // '' | 'customer' | 'host'
    const absorbsPosFees = useTextInput({ defaultValue: defaultValues?.absorbsPosFees ?? '' }); // '' | 'customer' | 'host'

    // Enable features
    const [enableFeeCaps, setEnableFeeCaps] = useState(defaultValues?.enableFeeCaps ?? false); // Enables feeMin, feeMax fields
    const [enableScheduledPayouts, setEnableScheduledPayouts] = useState(
        defaultValues?.enableScheduledPayouts ?? false,
    );

    function handleFeeCapToggle(e) {
        setEnableFeeCaps(e.target.checked);
        feeMin.setDisabled(!e.target.checked);
        feeMax.setDisabled(!e.target.checked);
    }

    function handleScheduledPayoutToggle(e) {
        setEnableScheduledPayouts(e.target.checked);
        reservePercent.setDisabled(!e.target.checked);
    }

    function handleSubmit() {
        if (loading) return;

        // Form Validation
        let formError = false;

        if (name.value.trim().length === 0) {
            name.setError('This field cannot be empty');
            formError = true;
        }

        if (absorbsB4tFees.value.trim().length === 0) {
            absorbsB4tFees.setError('This field cannot be empty');
            formError = true;
        }

        if (absorbsCcFees.value.trim().length === 0) {
            absorbsCcFees.setError('This field cannot be empty');
            formError = true;
        }

        if (absorbsPosFees.value.trim().length === 0) {
            absorbsPosFees.setError('This field cannot be empty');
            formError = true;
        }

        if (b4tFlat.value.length === 0) {
            b4tFlat.setError('This field cannot be empty');
            formError = true;
        }

        if (b4tPercent.value.length === 0) {
            b4tPercent.setError('This field cannot be empty');
            formError = true;
        }

        if (enableFeeCaps) {
            if (feeMin.value.length === 0) {
                feeMin.setError('This field cannot be empty');
                formError = true;
            }
            if (feeMax.value.length === 0) {
                feeMax.setError('This field cannot be empty');
                formError = true;
            }
            if (feeMin.value.length > 0 && feeMax.value.length > 0) {
                if (isNaN(feeMin.value) === false && isNaN(feeMax.value) === false) {
                    if (Number(feeMin.value) > Number(feeMax.value)) {
                        feeMax.setError('Maximum must be higher than minimum');
                        formError = true;
                    }
                }
            }
        }

        if (ccPercent.value.length === 0) {
            ccPercent.setError('This field cannot be empty');
            formError = true;
        }

        if (ccFlatFee.value.length === 0) {
            ccFlatFee.setError('This field cannot be empty');
            formError = true;
        }

        if (posPercent.value.length === 0) {
            posPercent.setError('This field cannot be empty');
            formError = true;
        }

        if (enableScheduledPayouts) {
            if (reservePercent.value.length === 0) {
                reservePercent.setError('This field cannot be empty');
                formError = true;
            }
        }

        if (formError) {
            return;
        }

        const formData = {
            name: name.value.trim(),
            feePerItem: Number(b4tFlat.value || 0),
            percentFeePerOrder: Number(
                b4tPercent.value ? (Number(b4tPercent.value) / 100).toPrecision(6) : 0,
            ),
            feeMinAmount: Number(feeMin.value || 0),
            feeMaxAmount: Number(feeMax.value || 0),
            ccPercent: Number((Number(ccPercent.value) / 100).toPrecision(6)),
            ccFlatFee: Number(ccFlatFee.value),
            posPercent: Number((Number(posPercent.value) / 100).toPrecision(6)),
            hostAbsorbsB4tFees: absorbsB4tFees.value === 'host' ? 1 : 0,
            hostAbsorbsCcFees: absorbsCcFees.value === 'host' ? 1 : 0,
            hostAbsorbsPosB4tFees: absorbsPosFees.value === 'host' ? 1 : 0,
            reservePercent: Number((Number(reservePercent.value) / 100).toFixed(4)),
            scheduledPayouts: Number(enableScheduledPayouts), // 1 or 0
        };

        onSubmit(formData);
    }

    return (
        <Box width={1} display="flex" flexDirection="column" alignItems="center">
            <Paper style={{ width: '100%' }}>
                <Box pb={1} pt={1}>
                    <Typography variant="h6" style={{ textAlign: 'center' }}>
                        {hostName}
                    </Typography>
                </Box>
            </Paper>

            <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                p={[2, 4]}
                maxWidth={theme.breakpoints.values.lg}
            >
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Section>
                            <Header>Name</Header>
                            <Content>
                                <TextField
                                    label="Name"
                                    variant="outlined"
                                    fullWidth
                                    {...name.formProps}
                                />
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Section>
                            <Header>Online - B4T Fees</Header>
                            <Typography>Fees are applied to each ticket</Typography>

                            <Content>
                                <RadioGroup
                                    value={absorbsB4tFees.formProps.value}
                                    onChange={absorbsB4tFees.formProps.onChange}
                                >
                                    <FormControlLabel
                                        value="customer"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Customer Pays"
                                    />
                                    <FormControlLabel
                                        value="host"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Host Pays"
                                    />
                                </RadioGroup>

                                {absorbsB4tFees.formProps.error && (
                                    <FormHelperText error>
                                        {absorbsB4tFees.formProps.helperText}
                                    </FormHelperText>
                                )}

                                <Box mt={4} />

                                <Box display="flex" alignItems="center" mt={1}>
                                    <TextField
                                        placeholder="Amount"
                                        variant="outlined"
                                        {...b4tFlat.formProps}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                    />
                                    <PlusIcon variant="h4">+</PlusIcon>
                                    <TextField
                                        placeholder="Percent"
                                        variant="outlined"
                                        {...b4tPercent.formProps}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box mt={4}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={enableFeeCaps}
                                                disabled={!allowEdit}
                                                onChange={handleFeeCapToggle}
                                            />
                                        }
                                        label="Set Fee Limits"
                                    />
                                    <Box display="flex" mt={1}>
                                        <TextField
                                            label="Minimum"
                                            variant="outlined"
                                            {...feeMin.formProps}
                                        />
                                        <Box width={28} />
                                        <TextField
                                            label="Maximum"
                                            variant="outlined"
                                            {...feeMax.formProps}
                                        />
                                    </Box>
                                </Box>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Section>
                            <Header>Online - Credit Card Fees</Header>
                            <Typography>Fees are applied to the whole order</Typography>
                            <Content>
                                <RadioGroup
                                    value={absorbsCcFees.formProps.value}
                                    onChange={absorbsCcFees.formProps.onChange}
                                >
                                    <FormControlLabel
                                        value="customer"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Customer Pays"
                                    />
                                    <FormControlLabel
                                        value="host"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Host Pays"
                                    />
                                </RadioGroup>

                                {absorbsCcFees.formProps.error && (
                                    <FormHelperText error>
                                        {absorbsCcFees.formProps.helperText}
                                    </FormHelperText>
                                )}

                                <Box mt={4} />

                                <Box display="flex" alignItems="center" mt={1}>
                                    <TextField
                                        placeholder="Percent"
                                        variant="outlined"
                                        {...ccPercent.formProps}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                    />
                                    <PlusIcon variant="h4">+</PlusIcon>
                                    <TextField
                                        placeholder="Amount"
                                        variant="outlined"
                                        {...ccFlatFee.formProps}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">$</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Section>
                            <Header>In Person - Unified Fee</Header>
                            <Typography>Fee is applied to the whole order</Typography>
                            <Content>
                                <RadioGroup
                                    value={absorbsPosFees.formProps.value}
                                    onChange={absorbsPosFees.formProps.onChange}
                                >
                                    <FormControlLabel
                                        value="customer"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Customer Pays"
                                    />
                                    <FormControlLabel
                                        value="host"
                                        disabled={!allowEdit}
                                        control={<Radio />}
                                        label="Host Pays"
                                    />
                                </RadioGroup>

                                {absorbsPosFees.formProps.error && (
                                    <FormHelperText error>
                                        {absorbsPosFees.formProps.helperText}
                                    </FormHelperText>
                                )}

                                <Box mt={4} />

                                <Box display="flex" alignItems="center" mt={1}>
                                    <TextField
                                        placeholder="Percent"
                                        variant="outlined"
                                        {...posPercent.formProps}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Scheduled Payouts</Header>

                            <Content>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={enableScheduledPayouts}
                                            disabled={!allowEdit}
                                            onChange={handleScheduledPayoutToggle}
                                        />
                                    }
                                    label="Enable Scheduled Payouts"
                                />

                                <Box maxWidth="50%" display="flex" flexDirection="column" mt={4}>
                                    <TextField
                                        {...reservePercent.formProps}
                                        label="Reserve"
                                        variant="outlined"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Content>
                        </Section>
                    </Grid>
                </Grid>

                <Footer>
                    {allowDelete && (
                        <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={loading}>
                            Delete
                        </DeleteButton>
                    )}
                    <CancelButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </CancelButton>
                    <SubmitButton onClick={handleSubmit} disabled={loading}>
                        Save
                    </SubmitButton>
                </Footer>
            </Box>
        </Box>
    );
}

const PlusIcon = styled(Typography)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
}));

const Footer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(4),
}));
